import type { CSSProperties, PointerEvent } from 'react'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import { DrawerSlideUpAnimation } from 'LuxKit/Modal/ModalSheet'
import { MODAL_TRANSITION_DURATION } from 'LuxKit/Modal/modalConstants'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import { breakpointSizes, mediaQueryDown } from 'components/utils/breakpoint'
import noop from 'lib/function/noop'
import ChatMapOfferCard from './ChatMapOfferCard'
import ChatMapOffersDrawerSkeleton from './ChatMapOffersDrawerSkeleton'
import type { ExploreOffer } from 'tripPlanner/api/explore/types'
import type { TripItineraryData } from 'tripPlanner/api/types'
import productLabels from 'i18n/productLabels'

export const OFFERS_DRAWER_WIDTH = 380
const MOBILE_OFFERS_DRAWER_HEIGHT = 'clamp(16rem, 36vh, 20rem)'
const MOBILE_OFFERS_DRAWER_MIN_HEIGHT = 256
const MOBILE_OFFERS_DRAWER_MAX_HEIGHT = 320
const MOBILE_OFFERS_DRAWER_MID_RATIO = 0.62
const MOBILE_OFFERS_DRAWER_FULL_OFFSET = 16
const MOBILE_MEDIA_QUERY = `(max-width: ${breakpointSizes.mobile.max}px)`
const DRAG_CLICK_THRESHOLD = 4

type MobileDrawerSnap = 'short' | 'mid' | 'full'
const MOBILE_DRAWER_SNAPS: ReadonlyArray<MobileDrawerSnap> = ['short', 'mid', 'full']

interface DragState {
  startY: number
  startHeight: number
}

interface DrawerStyle extends CSSProperties {
  '--chat-map-offers-drawer-height'?: string
}

interface DrawerWrapperProps {
  inert?: '' | undefined
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function getShortHeight(viewportHeight: number) {
  return clamp(viewportHeight * 0.36, MOBILE_OFFERS_DRAWER_MIN_HEIGHT, MOBILE_OFFERS_DRAWER_MAX_HEIGHT)
}

function getMobileSnapHeights(parentHeight: number) {
  const fullHeight = Math.max(getShortHeight(parentHeight), parentHeight - MOBILE_OFFERS_DRAWER_FULL_OFFSET)

  return {
    short: getShortHeight(parentHeight),
    mid: clamp(parentHeight * MOBILE_OFFERS_DRAWER_MID_RATIO, getShortHeight(parentHeight), fullHeight),
    full: fullHeight,
  } satisfies Record<MobileDrawerSnap, number>
}

function getClosestSnap(currentHeight: number, snapHeights: Record<MobileDrawerSnap, number>): MobileDrawerSnap {
  return MOBILE_DRAWER_SNAPS.reduce<MobileDrawerSnap>(
    (closestSnap, snap) => {
      const closestDistance = Math.abs(currentHeight - snapHeights[closestSnap])
      const nextDistance = Math.abs(currentHeight - snapHeights[snap])
      return nextDistance < closestDistance ? snap : closestSnap
    },
    'short',
  )
}

function getNextSnap(snap: MobileDrawerSnap): MobileDrawerSnap {
  if (snap === 'short') return 'mid'
  if (snap === 'mid') return 'full'
  return 'short'
}

const DrawerWrapper = styled.div<DrawerWrapperProps>`
  flex-shrink: 0;
  width: 0;

  &.open {
    width: ${rem(OFFERS_DRAWER_WIDTH)};
  }

  ${mediaQueryDown.mobile} {
    position: absolute;
    inset-inline: 0;
    bottom: 0;
    z-index: 7;
    width: auto;
    height: var(--chat-map-offers-drawer-height, ${MOBILE_OFFERS_DRAWER_HEIGHT});
    pointer-events: none;
    transition: height ${MODAL_TRANSITION_DURATION}ms ease-out;

    &.dragging {
      transition: none;
    }

    &.open {
      width: auto;
      pointer-events: auto;
    }
  }
`

const Panel = styled.div`
  --chat-map-offers-drawer-offset: 100%;

  width: ${rem(OFFERS_DRAWER_WIDTH)};
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-inline-start: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  transform: translateX(var(--chat-map-offers-drawer-offset));
  will-change: transform;
  transition: transform ${MODAL_TRANSITION_DURATION}ms ease-out;

  [dir="rtl"] & {
    --chat-map-offers-drawer-offset: -100%;
  }

  .open > & {
    transform: translateX(0);
  }

  ${mediaQueryDown.mobile} {
    width: 100%;
    height: 100%;
    border-inline-start: 0;
    border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    border-radius: ${rem(20)} ${rem(20)} 0 0;
    box-shadow: ${props => props.theme.shadow.flat.large};
    position: relative;
    transform: translateY(calc(100% + ${rem(16)}));
    animation-duration: ${MODAL_TRANSITION_DURATION}ms;
    animation-fill-mode: both;
    animation-timing-function: ease-out;

    .open > & {
      transform: translateY(0);
    }

    .open:not(.entered) > & {
      animation-name: ${DrawerSlideUpAnimation};
    }

    .dragging > & {
      animation: none;
    }
  }
`

const DragRegion = styled.div`
  display: contents;

  ${mediaQueryDown.mobile} {
    cursor: grab;
    display: block;
    touch-action: none;

    &:active {
      cursor: grabbing;
    }
  }
`

const DragHandle = styled.button.attrs({ type: 'button' })`
  display: none;

  ${mediaQueryDown.mobile} {
    appearance: none;
    background: transparent;
    border: 0;
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    padding: ${rem(10)} 0 ${rem(6)};
    width: 100%;

    &::before {
      content: '';
      width: ${rem(48)};
      height: ${rem(4)};
      border-radius: ${props => props.theme.borderRadius.round};
      background-color: ${props => props.theme.palette.neutral.default.five};
    }
  }
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: ${rem(16)} ${rem(20)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  flex-shrink: 0;
  gap: ${rem(16)};

  ${mediaQueryDown.mobile} {
    padding: 0 ${rem(20)} ${rem(12)};
  }
`

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  flex: 1;
  min-width: 0;
`

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;

  ${mediaQueryDown.mobile} {
    overscroll-behavior: contain;
  }
`

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  padding: ${rem(16)};

  ${mediaQueryDown.mobile} {
    padding: ${rem(12)} ${rem(16)} ${rem(16)};
  }
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${rem(48)} ${rem(24)};
  text-align: center;
`

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${rem(48)} ${rem(24)};
  text-align: center;
  gap: ${rem(16)};
`

interface Props {
  isOpen: boolean
  onClose: () => void
  offers: Array<ExploreOffer>
  offerType: 'hotel' | 'experience'
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  itineraryData?: TripItineraryData
  onSendMessage: (message: string) => void
  onViewOffer: (data: { offerId: string; offerType?: string }) => void
  highlightedMarkerId?: string
  onCardHover?: (offerId: string | undefined) => void
}

function ChatMapOffersDrawer({
  isOpen,
  onClose,
  offers,
  offerType,
  isLoading,
  isError,
  onRetry,
  itineraryData,
  onSendMessage,
  onViewOffer,
  highlightedMarkerId,
  onCardHover = noop,
}: Props) {
  const drawerWrapperRef = useRef<HTMLDivElement>(null)
  const dragStateRef = useRef<DragState | undefined>(undefined)
  const dragHeightRef = useRef<number | undefined>(undefined)
  const didDragRef = useRef(false)
  const [mobileSnap, setMobileSnap] = useState<MobileDrawerSnap>('short')
  const [dragHeight, setDragHeight] = useState<number | undefined>(undefined)
  const [isDragging, setIsDragging] = useState(false)
  const [hasEntered, setHasEntered] = useState(false)
  const [parentHeight, setParentHeight] = useState(MOBILE_OFFERS_DRAWER_MAX_HEIGHT)

  const isMobileDrawer = useCallback(() => {
    return typeof window !== 'undefined' && window.matchMedia(MOBILE_MEDIA_QUERY).matches
  }, [])

  const getCurrentParentHeight = useCallback(() => {
    const parentHeight = drawerWrapperRef.current?.parentElement?.clientHeight
    if (parentHeight && parentHeight > 0) {
      return parentHeight
    }

    return typeof window !== 'undefined' ? window.innerHeight : MOBILE_OFFERS_DRAWER_MAX_HEIGHT
  }, [])

  const updateParentHeight = useCallback(() => {
    setParentHeight(getCurrentParentHeight())
  }, [getCurrentParentHeight])

  const getSnapHeights = useCallback(() => getMobileSnapHeights(parentHeight), [parentHeight])

  const snapTo = useCallback((snap: MobileDrawerSnap) => {
    setMobileSnap(snap)
    setDragHeight(undefined)
  }, [])

  useEffect(() => {
    if (!isOpen || !isMobileDrawer()) return

    updateParentHeight()

    const parentElement = drawerWrapperRef.current?.parentElement
    const resizeObserver = parentElement && typeof ResizeObserver !== 'undefined' ?
        new ResizeObserver(updateParentHeight) :
      undefined

    if (resizeObserver && parentElement) {
      resizeObserver.observe(parentElement)
    }
    window.addEventListener('resize', updateParentHeight)

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', updateParentHeight)
    }
  }, [isMobileDrawer, isOpen, updateParentHeight])

  const handleDragStart = useCallback((event: PointerEvent<HTMLElement>) => {
    if (!isOpen || !isMobileDrawer()) return

    dragStateRef.current = {
      startY: event.clientY,
      startHeight: drawerWrapperRef.current?.getBoundingClientRect().height ?? getSnapHeights()[mobileSnap],
    }
    dragHeightRef.current = undefined
    didDragRef.current = false
    setIsDragging(true)
  }, [getSnapHeights, isMobileDrawer, isOpen, mobileSnap])

  useEffect(() => {
    if (!isDragging) return

    const handlePointerMove = (event: globalThis.PointerEvent) => {
      const dragState = dragStateRef.current
      if (!dragState) return

      event.preventDefault()
      const snapHeights = getSnapHeights()
      const nextHeight = clamp(
        dragState.startHeight + dragState.startY - event.clientY,
        snapHeights.short,
        snapHeights.full,
      )

      didDragRef.current = Math.abs(event.clientY - dragState.startY) > DRAG_CLICK_THRESHOLD
      dragHeightRef.current = nextHeight
      setDragHeight(nextHeight)
    }

    const handlePointerEnd = () => {
      const snapHeights = getSnapHeights()
      const currentHeight = dragHeightRef.current ?? drawerWrapperRef.current?.getBoundingClientRect().height ?? snapHeights.short

      snapTo(getClosestSnap(currentHeight, snapHeights))
      dragStateRef.current = undefined
      dragHeightRef.current = undefined
      setIsDragging(false)
      window.requestAnimationFrame(() => {
        didDragRef.current = false
      })
    }

    document.addEventListener('pointermove', handlePointerMove)
    document.addEventListener('pointerup', handlePointerEnd)
    document.addEventListener('pointercancel', handlePointerEnd)

    return () => {
      document.removeEventListener('pointermove', handlePointerMove)
      document.removeEventListener('pointerup', handlePointerEnd)
      document.removeEventListener('pointercancel', handlePointerEnd)
    }
  }, [getSnapHeights, isDragging, snapTo])

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen) {
      snapTo('short')
      setIsDragging(false)
      setHasEntered(false)
      dragStateRef.current = undefined
      dragHeightRef.current = undefined
    }
  }, [isOpen, snapTo])

  useEffect(() => {
    if (!isOpen) return

    const timeout = window.setTimeout(() => setHasEntered(true), MODAL_TRANSITION_DURATION)

    return () => window.clearTimeout(timeout)
  }, [isOpen])

  const handleDragHandleClick = useCallback(() => {
    if (didDragRef.current) {
      didDragRef.current = false
      return
    }

    snapTo(getNextSnap(mobileSnap))
  }, [mobileSnap, snapTo])

  const handleClosePointerDown = useCallback((event: PointerEvent<HTMLElement>) => {
    event.stopPropagation()
  }, [])

  const drawerHeight = dragHeight ?? (isOpen ? getSnapHeights()[mobileSnap] : undefined)
  const drawerStyle = useMemo<DrawerStyle | undefined>(() => {
    if (drawerHeight === undefined) return undefined

    return {
      '--chat-map-offers-drawer-height': rem(drawerHeight),
    }
  }, [drawerHeight])

  const intl = useIntl()

  const offersHeading = offerType === 'experience'
    ? intl.formatMessage({
        id: 'mdah2gC105p2yHp6epsS',
        defaultMessage: 'Experiences',
        description: 'Section heading for the map offers drawer listing experiences',
      })
    : intl.formatMessage(productLabels.hotels)

  const closePanelLabel = intl.formatMessage({
    id: 'ksE-HAW1PsIC7L4s6cLD',
    defaultMessage: 'Close panel',
    description: 'Aria-label for the close button on the map offers drawer',
  })

  const resizePanelLabel = intl.formatMessage({
    id: 'Zz3bmkDQXBG920b77Mto',
    defaultMessage: 'Resize map results panel',
    description: 'Aria-label for the drag handle used to resize the mobile map offers drawer',
  })

  const errorMessage = offerType === 'experience'
    ? intl.formatMessage({
        id: 'PystxXcZkTlt6NK4ILqq',
        defaultMessage: 'Something went wrong loading experiences.',
        description: 'Error message shown when experience offers fail to load in the map drawer',
      })
    : intl.formatMessage({
        id: '9xNuYzIT33aItyjX_RDp',
        defaultMessage: 'Something went wrong loading hotels.',
        description: 'Error message shown when hotel offers fail to load in the map drawer',
      })

  const tryAgainLabel = intl.formatMessage({
    id: '_84xgIWalzdd0QZkvLTp',
    defaultMessage: 'Try again',
    description: 'Button label to retry loading map drawer offers after an error',
  })

  const resultsCountLabel = intl.formatMessage(
    {
      id: '3-GCnO1r8Qlb4W2MVOlO',
      defaultMessage: '{count, plural, one {# result} other {# results}}',
      description: 'Results count label in the map offers drawer header',
    },
    { count: offers.length },
  )

  const emptyStateMessage = offerType === 'experience'
    ? intl.formatMessage({
        id: 'aDJHGhRheQ-wfqFZqEBb',
        defaultMessage: 'No experiences found in this area. Try panning or zooming the map.',
        description: 'Empty state message when no experience offers are found in the current map area',
      })
    : intl.formatMessage({
        id: 'gzI1eSJXgCbhpntmoK41',
        defaultMessage: 'No hotels found in this area. Try panning or zooming the map.',
        description: 'Empty state message when no hotel offers are found in the current map area',
      })

  return (
    <DrawerWrapper
      ref={drawerWrapperRef}
      className={clsx({ open: isOpen, dragging: isDragging, entered: hasEntered })}
      aria-hidden={!isOpen}
      inert={!isOpen ? '' : undefined}
      data-testid="chat-map-offers-drawer"
      data-mobile-snap={mobileSnap}
      style={drawerStyle}
    >
      <Panel data-testid="chat-map-offers-drawer-panel">
        <DragRegion data-testid="chat-map-offers-drawer-drag-region">
          <DragHandle
            aria-label={resizePanelLabel}
            onPointerDown={handleDragStart}
            onClick={handleDragHandleClick}
            data-testid="chat-map-offers-drawer-drag-handle"
          />
          <Header onPointerDown={handleDragStart}>
            <HeaderContent>
              <Heading variant="heading5">{offersHeading}</Heading>
              {!isLoading && !isError && (
                <BodyText variant="small" colour="light-foreground-alt">
                  {resultsCountLabel}
                </BodyText>
              )}
            </HeaderContent>
            <IconButton
              kind="tertiary"
              variant="dark"
              size="medium"
              onClick={onClose}
              onPointerDown={handleClosePointerDown}
              aria-label={closePanelLabel}
            >
              <LineTimesIcon />
            </IconButton>
          </Header>
        </DragRegion>
        <Body data-testid="chat-map-offers-drawer-body">
          {isLoading && <ChatMapOffersDrawerSkeleton />}
          {!isLoading && isError && (
            <ErrorState>
              <Group direction="vertical" gap={8} horizontalAlign="center">
                <BodyText variant="medium" colour="light-foreground-alt">
                  {errorMessage}
                </BodyText>
                <TextButton
                  kind="secondary"
                  size="small"
                  onClick={onRetry}
                >
                  {tryAgainLabel}
                </TextButton>
              </Group>
            </ErrorState>
          )}
          {!isLoading && !isError && offers.length === 0 && (
            <EmptyState>
              <BodyText variant="medium" colour="light-foreground-alt">
                {emptyStateMessage}
              </BodyText>
            </EmptyState>
          )}
          {!isLoading && !isError && offers.length > 0 && (
            <CardList>
              {offers.map((offer) => (
                <ChatMapOfferCard
                  key={offer.id}
                  offer={offer}
                  itineraryData={itineraryData}
                  onSendMessage={onSendMessage}
                  onViewOffer={onViewOffer}
                  onHover={onCardHover}
                  isHighlighted={highlightedMarkerId === offer.id}
                />
              ))}
            </CardList>
          )}
        </Body>
      </Panel>
    </DrawerWrapper>
  )
}

export default memo(ChatMapOffersDrawer)
